import { useState, useEffect } from "react";
import { Layout } from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../contexts/LanguageContext";
import { fetchAPI } from "../lib/utils";
import {
	isPushSupported,
	hasLocalPushSubscription,
	syncLocalPushSubscriptionToServer,
	subscribeUserToPush,
	unsubscribeUserFromPush,
} from "../lib/pushNotifications";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	CardDescription,
} from "../components/ui/card";
import { PushNotificationSettingsCard } from "../components/PushNotificationSettingsCard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
	Settings as SettingsIcon,
	Globe,
	Bell,
	Download,
	Smartphone,
	Check,
	Mail,
} from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
	const { user } = useAuth();
	const { language, setLanguage, t } = useLanguage();
	const [deferredPrompt, setDeferredPrompt] = useState(null);
	const [isInstalled, setIsInstalled] = useState(false);
	const [notifPrefs, setNotifPrefs] = useState({
		email_new_program: false,
		email_new_message: false,
		email_workout_reminder: false,
		push_enabled: false,
		push_new_message: true,
		push_new_program_and_week: true,
		push_ever_enabled: false,
	});
	const [savingNotifs, setSavingNotifs] = useState(false);
	const [pushSupported, setPushSupported] = useState(false);
	const [pushServerConfigured, setPushServerConfigured] = useState(false);
	const [pushBusy, setPushBusy] = useState(false);
	const [emailFormOpen, setEmailFormOpen] = useState(false);
	const [newEmail, setNewEmail] = useState("");
	const [confirmNewEmail, setConfirmNewEmail] = useState("");
	const [currentPassword, setCurrentPassword] = useState("");
	const [changingEmail, setChangingEmail] = useState(false);

	useEffect(() => {
		setPushSupported(isPushSupported());
		fetchAPI("/users/me/push-config")
			.then((cfg) => setPushServerConfigured(!!cfg?.push_available))
			.catch(() => setPushServerConfigured(false));

		// Check if app is installed
		if (window.matchMedia("(display-mode: standalone)").matches) {
			setIsInstalled(true);
		}

		// Listen for install prompt
		const handleBeforeInstall = (e) => {
			e.preventDefault();
			setDeferredPrompt(e);
		};

		window.addEventListener("beforeinstallprompt", handleBeforeInstall);

		loadNotificationPrefs();

		return () => {
			window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
		};
	}, []);

	const loadNotificationPrefs = async () => {
		const token = localStorage.getItem("session_token");
		console.log(
			"Loading notif prefs, has token:",
			!!token,
			"token length:",
			token?.length || 0,
		);
		try {
			const [prefs, localPushSubscribed] = await Promise.all([
				fetchAPI("/users/me/notifications"),
				hasLocalPushSubscription(),
			]);
			if (localPushSubscribed && !prefs?.push_enabled) {
				await syncLocalPushSubscriptionToServer().catch(() => {});
			}
			const effectivePushEnabled =
				!!prefs?.push_enabled || !!localPushSubscribed;
			setNotifPrefs((prev) => ({
				...prev,
				...prefs,
				push_enabled: effectivePushEnabled,
				push_ever_enabled: !!prefs?.push_ever_enabled || effectivePushEnabled,
				email_new_program: false,
				email_new_message: false,
				email_workout_reminder: false,
			}));
		} catch (e) {
			console.error(
				"Error loading notification prefs:",
				e?.status,
				e?.detail || e?.message,
			);
		}
	};

	const saveNotificationPrefs = async (newPrefs) => {
		setSavingNotifs(true);
		const payload = {
			...newPrefs,
			email_new_program: false,
			email_new_message: false,
			email_workout_reminder: false,
		};
		try {
			await fetchAPI("/users/me/notifications", {
				method: "PUT",
				body: JSON.stringify(payload),
			});
			setNotifPrefs(payload);
			window.dispatchEvent(new Event("push-onboarding-updated"));
			toast.success(
				language === "it" ? "Preferenze salvate!" : "Preferences saved!",
			);
		} catch (e) {
			toast.error("Error saving preferences");
		} finally {
			setSavingNotifs(false);
		}
	};

	const handleNotifChange = (key, value) => {
		const newPrefs = { ...notifPrefs, [key]: value };
		saveNotificationPrefs(newPrefs);
	};

	const hasPushStateMismatch = async (enabled) => {
		const [serverPrefs, localPushSubscribed] = await Promise.all([
			fetchAPI("/users/me/notifications").catch(() => null),
			hasLocalPushSubscription().catch(() => false),
		]);

		if (enabled) {
			return !(!!serverPrefs?.push_enabled || !!localPushSubscribed);
		}

		// Consider disabled if either server preference is off OR local subscription is gone.
		return !!serverPrefs?.push_enabled && !!localPushSubscribed;
	};

	const handlePushEnabledChange = async (enabled) => {
		if (!pushSupported) {
			toast.error(
				language === "it"
					? "Push non supportate su questo browser"
					: "Push not supported on this browser",
			);
			return;
		}
		if (!pushServerConfigured) {
			toast.error(
				language === "it"
					? "Push non configurate sul server"
					: "Push not configured on server",
			);
			return;
		}

		setPushBusy(true);
		let subscriptionError = null;

		// Try to update subscription, but don't fail the whole operation if it fails
		if (enabled) {
			try {
				const localPushSubscribed = await hasLocalPushSubscription();
				if (!localPushSubscribed) {
					await subscribeUserToPush();
				} else {
					await syncLocalPushSubscriptionToServer().catch(() => {});
				}
			} catch (e) {
				subscriptionError = e;
				console.warn(
					"Subscription failed but will still save preference:",
					subscriptionError,
				);
			}
		} else {
			try {
				await unsubscribeUserFromPush();
			} catch (e) {
				subscriptionError = e;
				console.warn(
					"Unsubscribe failed but will still save preference:",
					subscriptionError,
				);
			}
		}

		// Always save preferences regardless of subscription result
		const nextPrefs = {
			...notifPrefs,
			push_enabled: enabled,
			push_ever_enabled: !!notifPrefs.push_ever_enabled || enabled,
		};

		try {
			await saveNotificationPrefs(nextPrefs);
			// Show warning only if final effective state is actually inconsistent.
			if (subscriptionError && (await hasPushStateMismatch(enabled))) {
				toast.info(
					language === "it"
						? "Preferenza salvata ma la registrazione della notifica ha avuto problemi. Riprova se necessario."
						: "Preference saved but notification registration failed. Please retry if needed.",
				);
			} else if (subscriptionError) {
				console.warn(
					"Transient push subscription error ignored because effective state is correct.",
					subscriptionError,
				);
			}
		} catch (e) {
			toast.error(
				e?.message ||
					(language === "it"
						? "Impossibile aggiornare le notifiche push"
						: "Could not update push notifications"),
			);
		} finally {
			setPushBusy(false);
		}
	};

	const handleInstallApp = async () => {
		if (!deferredPrompt) {
			toast.info(
				language === "it"
					? 'Per installare, usa il menu del browser e seleziona "Aggiungi alla schermata Home"'
					: 'To install, use the browser menu and select "Add to Home Screen"',
			);
			return;
		}

		deferredPrompt.prompt();
		const { outcome } = await deferredPrompt.userChoice;

		if (outcome === "accepted") {
			toast.success(language === "it" ? "App installata!" : "App installed!");
			setIsInstalled(true);
		}

		setDeferredPrompt(null);
	};

	const handleClearCache = async () => {
		if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
			navigator.serviceWorker.controller.postMessage({ type: "CLEAR_CACHE" });
			toast.success(language === "it" ? "Cache svuotata!" : "Cache cleared!");
		}
	};

	const handleChangeEmail = async (e) => {
		e.preventDefault();
		if (
			newEmail.trim().toLowerCase() !== confirmNewEmail.trim().toLowerCase()
		) {
			toast.error(t("emailsDoNotMatch"));
			return;
		}
		setChangingEmail(true);
		try {
			const res = await fetchAPI("/users/change-email/request", {
				method: "POST",
				body: JSON.stringify({
					new_email: newEmail.trim(),
					current_password: currentPassword,
				}),
			});
			toast.success(res?.message || t("changeEmailSent"));
			if (res && res.email_enabled === false) {
				toast.info(t("changeEmailEmailDisabled"));
			}
			setEmailFormOpen(false);
			setNewEmail("");
			setConfirmNewEmail("");
			setCurrentPassword("");
		} catch (err) {
			toast.error(err?.message || t("genericError"));
		} finally {
			setChangingEmail(false);
		}
	};

	return (
		<Layout>
			<div
				className="max-w-3xl mx-auto px-4 md:px-6 py-6 md:py-8"
				data-testid="settings-page"
			>
				<div className="mb-8">
					<h1 className="font-heading text-3xl md:text-4xl mb-2 flex items-center gap-3">
						<SettingsIcon className="w-8 h-8 text-volt" />
						{t("settings")}
					</h1>
					<p className="text-muted-foreground">
						{language === "it"
							? "Gestisci le preferenze dell'app"
							: "Manage app preferences"}
					</p>
				</div>

				<div className="space-y-6">
					<PushNotificationSettingsCard
						testId="push-notification-settings"
						notifPrefs={notifPrefs}
						pushSupported={pushSupported}
						pushServerConfigured={pushServerConfigured}
						pushBusy={pushBusy}
						savingNotifs={savingNotifs}
						onPushEnabledChange={handlePushEnabledChange}
						onNotifChange={handleNotifChange}
					/>

					{/* Language Settings */}
					<Card
						className="bg-card border-border rounded-sm"
						data-testid="language-settings"
					>
						<CardHeader>
							<CardTitle className="font-heading text-xl flex items-center gap-2">
								<Globe className="w-5 h-5 text-volt" />
								{language === "it" ? "Lingua" : "Language"}
							</CardTitle>
							<CardDescription>
								{language === "it"
									? "Seleziona la lingua dell'interfaccia"
									: "Select interface language"}
							</CardDescription>
						</CardHeader>
						<CardContent>
							<div className="flex gap-3">
								<Button
									variant={language === "it" ? "default" : "outline"}
									className={`flex-1 rounded-sm ${language === "it" ? "bg-volt hover:bg-volt/90" : ""}`}
									onClick={() => setLanguage("it")}
									data-testid="lang-it-btn"
								>
									{language === "it" && <Check className="w-4 h-4 mr-2" />}
									🇮🇹 Italiano
								</Button>
								<Button
									variant={language === "en" ? "default" : "outline"}
									className={`flex-1 rounded-sm ${language === "en" ? "bg-volt hover:bg-volt/90" : ""}`}
									onClick={() => setLanguage("en")}
									data-testid="lang-en-btn"
								>
									{language === "en" && <Check className="w-4 h-4 mr-2" />}
									🇬🇧 English
								</Button>
							</div>
						</CardContent>
					</Card>

					{/* Mobile App Installation */}
					<Card
						className="bg-card border-border rounded-sm"
						data-testid="mobile-settings"
					>
						<CardHeader>
							<CardTitle className="font-heading text-xl flex items-center gap-2">
								<Smartphone className="w-5 h-5 text-acid" />
								{language === "it" ? "App Mobile" : "Mobile App"}
							</CardTitle>
							<CardDescription>
								{language === "it"
									? "Installa l'app per un'esperienza ottimale su mobile"
									: "Install the app for optimal mobile experience"}
							</CardDescription>
						</CardHeader>
						<CardContent className="space-y-4">
							{isInstalled ? (
								<div className="flex items-center gap-2 text-acid">
									<Check className="w-5 h-5" />
									<span>
										{language === "it" ? "App installata" : "App installed"}
									</span>
								</div>
							) : (
								<Button
									onClick={handleInstallApp}
									className="w-full bg-acid hover:bg-acid/90 text-black rounded-sm"
									data-testid="install-app-btn"
								>
									<Download className="w-4 h-4 mr-2" />
									{t("installApp")}
								</Button>
							)}

							<div className="pt-4 border-t border-border">
								<p className="text-sm text-muted-foreground mb-3">
									{language === "it"
										? "L'app funziona anche offline! I tuoi allenamenti saranno disponibili anche senza connessione."
										: "The app works offline too! Your workouts will be available even without connection."}
								</p>
								<Button
									variant="outline"
									onClick={handleClearCache}
									className="rounded-sm"
									size="sm"
								>
									{language === "it" ? "Svuota Cache" : "Clear Cache"}
								</Button>
							</div>
						</CardContent>
					</Card>

					{/* Account Info */}
					<Card className="bg-card border-border rounded-sm">
						<CardHeader>
							<CardTitle className="font-heading text-xl">
								{language === "it" ? "Account" : "Account"}
							</CardTitle>
						</CardHeader>
						<CardContent className="space-y-3">
							<div className="flex justify-between items-center">
								<span className="text-muted-foreground">{t("email")}</span>
								<span>{user?.email}</span>
							</div>
							{user?.has_password && (
								<div className="pt-1">
									{!emailFormOpen ? (
										<Button
											variant="outline"
											size="sm"
											onClick={() => setEmailFormOpen(true)}
											className="rounded-sm gap-2"
											data-testid="change-email-open-btn"
										>
											<Mail className="w-4 h-4" />
											{t("changeEmailCta")}
										</Button>
									) : (
										<form
											onSubmit={handleChangeEmail}
											className="space-y-3 rounded-sm border border-border/60 bg-secondary/30 p-3"
										>
											<p className="text-xs text-muted-foreground">
												{t("changeEmailHint")}
											</p>
											<div className="space-y-1.5">
												<Label htmlFor="new-email" className="label-tactical">
													{t("newEmail")}
												</Label>
												<Input
													id="new-email"
													type="email"
													placeholder="nome@email.com"
													value={newEmail}
													onChange={(e) => setNewEmail(e.target.value)}
													className="bg-secondary border-transparent h-11 rounded-sm"
													required
													data-testid="change-email-new-input"
												/>
											</div>
											<div className="space-y-1.5">
												<Label
													htmlFor="confirm-new-email"
													className="label-tactical"
												>
													{t("confirmEmail")}
												</Label>
												<Input
													id="confirm-new-email"
													type="email"
													placeholder="nome@email.com"
													value={confirmNewEmail}
													onChange={(e) => setConfirmNewEmail(e.target.value)}
													onPaste={(e) => e.preventDefault()}
													className="bg-secondary border-transparent h-11 rounded-sm"
													required
													data-testid="change-email-confirm-input"
												/>
											</div>
											<div className="space-y-1.5">
												<Label
													htmlFor="current-password"
													className="label-tactical"
												>
													{t("currentPassword")}
												</Label>
												<Input
													id="current-password"
													type="password"
													placeholder="••••••••"
													value={currentPassword}
													onChange={(e) => setCurrentPassword(e.target.value)}
													className="bg-secondary border-transparent h-11 rounded-sm"
													required
													data-testid="change-email-password-input"
												/>
											</div>
											<div className="flex gap-2">
												<Button
													type="submit"
													size="sm"
													disabled={changingEmail}
													className="rounded-sm bg-volt hover:bg-volt/90 text-white"
													data-testid="change-email-submit-btn"
												>
													{changingEmail
														? t("sending")
														: t("changeEmailSubmit")}
												</Button>
												<Button
													type="button"
													variant="ghost"
													size="sm"
													onClick={() => {
														setEmailFormOpen(false);
														setNewEmail("");
														setConfirmNewEmail("");
														setCurrentPassword("");
													}}
													className="rounded-sm"
												>
													{t("cancel")}
												</Button>
											</div>
										</form>
									)}
								</div>
							)}
							<div className="flex justify-between items-center">
								<span className="text-muted-foreground">{t("role")}</span>
								<span className="capitalize">
									{user?.role === "coach" ? t("coach") : t("athlete")}
								</span>
							</div>
							<div className="flex justify-between items-center">
								<span className="text-muted-foreground">{t("name")}</span>
								<span>{user?.name}</span>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>
		</Layout>
	);
}
